import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import './styles/app.css';
import './styles/global.scss';
import { Toaster } from 'sonner';
import { Suspense, useState, useEffect } from 'react';
import AppProviders from './context/AppProviders';
import { useSession } from './features/auth/context/Session';
import { defaultQueryClient } from './lib/tanstackQuery';
import { RouteErrorBoundary } from './components/errors/RouteErrorBoundary';
import { RouteNotFoundErrorBoundary } from './components/errors/RouteNotFoundErrorBoundary';
import { useOrganisation } from './hooks/useOrganisation';
import { Loading } from './components/Loading';
import { services } from './services';
import { Portal } from '@radix-ui/themes';
import { RadixThemeProvider } from './lib/radix/RadixThemeProvider';
import ReactGA from 'react-ga4';
import { GOOGLE_ANALYTICS_ID } from './utils/constants';

ReactGA.initialize(GOOGLE_ANALYTICS_ID);

// Set up a Router instance
const router = createRouter({
  routeTree,
  context: {
    session: null!,
    queryClient: defaultQueryClient,
    organisation: undefined,
    organisations: undefined,
    services: services,
  },
  // Fuzzy matching to preserve as much parent layout as possible for the user gives them more context to navigate to a useful location based on where they thought they would arrive.
  notFoundMode: 'fuzzy',
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouteErrorBoundary,
  defaultNotFoundComponent: RouteNotFoundErrorBoundary,
  // defaultPendingComponent: () => <Loading />,
});

// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function Router() {
  const { organisation, organisations } = useOrganisation();
  const { session } = useSession();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (session) {
      if (organisation) {
        setIsReady(true);
      }
    } else {
      setIsReady(true);
    }
  }, [organisation, session]);

  if (!isReady) {
    return null;
  }

  return <RouterProvider router={router} context={{ session, organisation, organisations }} />;
}

function App() {
  return (
    <AppProviders>
      <Suspense>
        <Router />
        <Portal>
          <RadixThemeProvider appearance="dark">
            <Toaster
              className="pointer-events-auto"
              position="top-right"
              style={{ zIndex: 1000 }}
              offset={{ top: '70px', right: '20px' }}
              closeButton
              richColors
            />
          </RadixThemeProvider>
        </Portal>
      </Suspense>
    </AppProviders>
  );
}

const rootElement = document.getElementById('app')!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
